import React, { useState, useEffect, useRef } from "react";
import Colors from "../../Constants/Colors";

const MatchedQuestionsView = ({
  currentQuestion,
  matchedQuestions = [],
  processText,
}) => {
  const [openIds, setOpenIds] = useState({});
  const [dividerPosition, setDividerPosition] = useState(48);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const pos = ((e.clientX - rect.left) / rect.width) * 100;
      setDividerPosition(Math.min(Math.max(pos, 25), 75));
    };
    const onUp = () => setIsDragging(false);
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, [isDragging]);

  useEffect(() => {
    setOpenIds({});
  }, [currentQuestion?.question_id]);

  const toggle = (id) => setOpenIds((p) => ({ ...p, [id]: !p[id] }));

  const popData = JSON.parse(localStorage.getItem("POP")) || {};
  const q = popData[currentQuestion?.question_id] || currentQuestion || {};
  const solution = q.explaination || "";

  const isCorrect = (opt) =>
    opt.parse_ques_option_correct_flag !== undefined
      ? opt.parse_ques_option_correct_flag === 1
      : opt.option_correct_flag === 1;

  const plain = (t) => String(t || "").replace(/<[^>]*>/g, "");

  // 95%+ green, usse kam orange
  const pctColor = (p) => (Number(p) >= 95 ? "#2E7D32" : "#F57C00");
  const pctBg = (p) => (Number(p) >= 95 ? "#E8F5E9" : "#FFF3E0");
  const pctBorder = (p) => (Number(p) >= 95 ? "#A5D6A7" : "#FFCC80");
  return (
    <div
      ref={containerRef}
      style={{
        display: "flex",
        gap: "20px",
        alignItems: "flex-start",
        width: "100%",
        padding: "10px",
        boxSizing: "border-box",
        userSelect: isDragging ? "none" : "auto",
      }}
    >
      {/* LEFT: current question preview */}
      <div style={{ flex: `0 0 ${dividerPosition}%`, minWidth: 0 }}>
        <div style={{ color: "#424342", padding: "6px 0" }}>Preview :</div>
        <div
          style={{
            border: "1px solid #ccc",
            background: "#fff",
            padding: "12px",
            minHeight: "100px",
          }}
        >
          <div style={{ color: "#424342", marginBottom: "8px" }}>Question:</div>
          <div
            style={{ padding: "8px" }}
            dangerouslySetInnerHTML={{ __html: processText(q.question_text) }}
          />

          {(q.options || []).map((opt, i) => (
            <div
              key={opt.option_id || i}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "10px",
                padding: "10px",
              }}
            >
              <strong style={{ flexShrink: 0, marginTop: "5px" }}>
                Option {i + 1}:
              </strong>
              <div
                style={{ flex: 1 }}
                dangerouslySetInnerHTML={{ __html: processText(opt.option_text) }}
              />
            </div>
          ))}

          <div style={{ marginTop: "20px", color: "#424342" }}>Solution:</div>
          <div
            style={{ padding: "10px" }}
            dangerouslySetInnerHTML={{ __html: processText(solution) }}
          />
        </div>
      </div>

      {/* DIVIDER */}
      <div
        onMouseDown={() => setIsDragging(true)}
        style={{
          position: "relative",
          alignSelf: "stretch",
          width: "10px",
          flexShrink: 0,
          marginTop: "26px",
          minHeight: "300px",
          cursor: "col-resize",
          borderLeft: "2px dotted #999",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "0px",
            width: 0,
            height: 0,
            borderTop: "6px solid transparent",
            borderBottom: "6px solid transparent",
            borderLeft: "6px solid #999",
            transform: "translateY(-50%)",
          }}
        />
      </div>

      {/* RIGHT: matched questions */}
      <div
        style={{
          flex: "1 1 52%",
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          paddingRight: "6px",
          marginTop: "26px",
        }}
      >
        {matchedQuestions.map((mq) => {
          const isOpen = !!openIds[mq.question_id];
          return (
            <div
              key={mq.question_id}
              style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}
            >
              {/* <div
                style={{
                  width: "60px",
                  flexShrink: 0,
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#E8719C",
                  paddingTop: "10px",
                }}
              >
                {mq.match_percentage}%
              </div> */}

              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  border: "1px solid #C4C4C4",
                  borderRadius: "10px",
                  background: "#fff",
                }}
              >
                <div
                  onClick={() => toggle(mq.question_id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "10px",
                    padding: "10px 12px",
                    cursor: "pointer",
                    background: `linear-gradient(to right, #C8E6C9 0%, #C8E6C9 ${mq.match_percentage}%, #FFE0B2 ${mq.match_percentage}%, #FFE0B2 100%)`,
                    borderRadius: isOpen ? "10px 10px 0 0" : "10px",
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: "13px",
                      color: "#B00020",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {isOpen ? `Question ID: ${mq.question_id}` : plain(mq.question_text)}
                  </div>
                  <span
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#2E7D32",
                      flexShrink: 0,
                    }}
                  >
                    {mq.match_percentage}%
                  </span>
                  <span
                    style={{
                      fontSize: "16px",
                      color: "#424342",
                      transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.2s",
                    }}
                  >
                    ▾
                  </span>
                </div>

                {isOpen && (
                  <div
                    style={{
                      padding: "0 12px 12px 12px",
                      borderTop: "1px solid #E5E5E5",
                    }}
                  >
                    <div
                      style={{ marginTop: "8px" }}
                      dangerouslySetInnerHTML={{
                        __html: processText(mq.question_text),
                      }}
                    />
                    <ul style={{ margin: "8px 0 0 0", padding: 0 }}>
                      {(mq.options || []).map((opt, i) => {
                        const correct = isCorrect(opt);
                        return (
                          <li
                            key={opt.option_id || i}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              listStyle: "none",
                              marginBottom: "6px",
                            }}
                          >
                            <input
                              type="radio"
                              checked={correct}
                              disabled
                              style={{
                                cursor: "not-allowed",
                                accentColor: correct
                                  ? `${Colors.color11}`
                                  : `${Colors.color4}`,
                              }}
                            />
                            <span
                              dangerouslySetInnerHTML={{
                                __html: processText(opt.option_text),
                              }}
                            />
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MatchedQuestionsView;